// SEO-EXPANSION v4: additive, no behavior change to existing pages.
export type IntentNode = {
  slug: string;
  exactKeyword: string;
  metal: "gold" | "silver" | "platinum" | "palladium";
  region: "pk" | "in" | "ae" | "sa" | "uk" | "us" | "global";
  city?: string;
  unit: "tola" | "gram" | "ounce" | "kg" | "masha" | "ratti";
  karat?: "24k" | "22k" | "21k" | "18k";
  currency: "PKR" | "INR" | "AED" | "SAR" | "USD" | "GBP";
  titlePattern: string;
  h1Pattern: string;
  canonicalSibling?: string;
  cohort: 1 | 2 | 3 | 4;
  intentCluster: "price-check" | "converter" | "historical" | "forecast" | "comparison";
};

type CitySpec = { slug: string; name: string; region: IntentNode["region"]; currency: IntentNode["currency"]; bazaar: string };

const CITIES: CitySpec[] = [
  { slug: "karachi", name: "Karachi", region: "pk", currency: "PKR", bazaar: "Sarafa Bazaar" },
  { slug: "lahore", name: "Lahore", region: "pk", currency: "PKR", bazaar: "Anarkali Bazaar" },
  { slug: "islamabad", name: "Islamabad", region: "pk", currency: "PKR", bazaar: "Jinnah Super Market" },
  { slug: "rawalpindi", name: "Rawalpindi", region: "pk", currency: "PKR", bazaar: "Raja Bazaar" },
  { slug: "mumbai", name: "Mumbai", region: "in", currency: "INR", bazaar: "Zaveri Bazaar" },
  { slug: "delhi", name: "Delhi", region: "in", currency: "INR", bazaar: "Karol Bagh" },
  { slug: "chennai", name: "Chennai", region: "in", currency: "INR", bazaar: "T. Nagar" },
  { slug: "dubai", name: "Dubai", region: "ae", currency: "AED", bazaar: "Gold Souk" },
];

export const CITY_BAZAAR: Record<string, string> = Object.fromEntries(CITIES.map((c) => [c.slug, c.bazaar]));
export const CITY_NAME: Record<string, string> = Object.fromEntries(CITIES.map((c) => [c.slug, c.name]));

const UNITS: IntentNode["unit"][] = ["tola", "gram", "ounce"];
const KARATS = ["24k", "22k", "21k", "18k"] as const;

function pcCohort(i: number): 1 | 2 | 3 | 4 {
  return ((i % 4) + 1) as 1 | 2 | 3 | 4;
}

function buildPriceCheck(): IntentNode[] {
  const out: IntentNode[] = [];
  let i = 0;
  for (const metal of ["gold", "silver"] as const) {
    for (const city of CITIES) {
      for (const unit of UNITS) {
        for (const karat of KARATS) {
          if (metal === "silver" && karat !== "24k") continue;
          const slug = `${metal}-rate-${city.slug}-${karat}-per-${unit}`;
          const canonicalSibling = slug;
          const kw = `${karat.toUpperCase()} ${metal[0].toUpperCase() + metal.slice(1)} Rate in ${city.name} per ${unit[0].toUpperCase() + unit.slice(1)}`;
          out.push({
            slug,
            exactKeyword: kw,
            metal,
            region: city.region,
            city: city.slug,
            unit,
            karat,
            currency: city.currency,
            titlePattern: "{exactKeyword} Today {YEAR}",
            h1Pattern: `Live ${karat.toUpperCase()} ${metal} price update for ${city.name} buyers`,
            canonicalSibling,
            cohort: pcCohort(i++),
            intentCluster: "price-check",
          });
        }
      }
    }
  }
  return out;
}

function buildConverter(): IntentNode[] {
  const pairs: Array<[IntentNode["unit"], IntentNode["unit"]]> = [
    ["tola", "gram"], ["gram", "tola"], ["ounce", "gram"], ["gram", "ounce"],
    ["tola", "ounce"], ["ounce", "tola"], ["masha", "gram"], ["ratti", "gram"],
  ];
  const out: IntentNode[] = [];
  let i = 0;
  for (const metal of ["gold", "silver"] as const) {
    for (const [a, b] of pairs) {
      const slug = `${metal}-${a}-to-${b}-converter`;
      out.push({
        slug,
        exactKeyword: `${metal[0].toUpperCase() + metal.slice(1)} ${a} to ${b} converter`,
        metal,
        region: "global",
        unit: a,
        currency: "USD",
        titlePattern: "{exactKeyword} — {YEAR} Live Calculator",
        h1Pattern: `Convert ${metal} weight from ${a} to ${b} instantly`,
        cohort: pcCohort(i++),
        intentCluster: "converter",
      });
    }
  }
  return out;
}

function buildHistorical(): IntentNode[] {
  const tfs: Array<{ slug: string; label: string }> = [
    { slug: "today", label: "Today" },
    { slug: "7-day", label: "7 Day" },
    { slug: "30-day", label: "30 Day" },
    { slug: "1-year", label: "1 Year" },
  ];
  const out: IntentNode[] = [];
  let i = 0;
  for (const metal of ["gold", "silver"] as const) {
    for (const region of ["pk", "in", "ae", "global"] as const) {
      for (const tf of tfs) {
        const slug = `${metal}-${tf.slug}-history-${region}`;
        const isForecast = tf.slug === "today";
        out.push({
          slug,
          exactKeyword: `${metal[0].toUpperCase() + metal.slice(1)} ${tf.label} ${isForecast ? "Forecast" : "History"} ${region.toUpperCase()}`,
          metal,
          region,
          unit: "ounce",
          currency: region === "pk" ? "PKR" : region === "in" ? "INR" : region === "ae" ? "AED" : "USD",
          titlePattern: "{exactKeyword} Chart — {YEAR}",
          h1Pattern: `${metal[0].toUpperCase() + metal.slice(1)} ${tf.label.toLowerCase()} ${isForecast ? "outlook" : "trend"} for ${region.toUpperCase()} market`,
          cohort: pcCohort(i++),
          intentCluster: isForecast ? "forecast" : "historical",
        });
      }
    }
  }
  return out;
}

export const INTENT_REGISTRY: IntentNode[] = [
  ...buildPriceCheck(),
  ...buildConverter(),
  ...buildHistorical(),
];

export function findIntent(slug: string): IntentNode | undefined {
  return INTENT_REGISTRY.find((n) => n.slug === slug);
}

export function siblingsFor(node: IntentNode, count = 4): IntentNode[] {
  return INTENT_REGISTRY.filter(
    (n) => n.slug !== node.slug && n.intentCluster === node.intentCluster && n.metal === node.metal,
  ).slice(0, count);
}

export function crossClusterFor(node: IntentNode, count = 2): IntentNode[] {
  return INTENT_REGISTRY.filter(
    (n) => n.metal === node.metal && n.intentCluster !== node.intentCluster,
  ).slice(0, count);
}